import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

let cachedDocs = null;
let cacheTimestamp = 0;
let inflightPromise = null;

/**
 * Fetches UnionCouncils docs once, caches for 5 minutes.
 * Concurrent callers share the same in-flight request.
 */
const fetchUCDocs = async () => {
  const now = Date.now();
  if (cachedDocs && now - cacheTimestamp < CACHE_TTL) {
    return cachedDocs;
  }
  if (inflightPromise) {
    return inflightPromise;
  }
  inflightPromise = (async () => {
    try {
      const snapshot = await getDocs(collection(db, 'UnionCouncils'));
      const docs = [];
      snapshot.forEach((doc) => {
        docs.push({ id: doc.id, ...doc.data() });
      });
      cachedDocs = docs;
      cacheTimestamp = Date.now();
      return docs;
    } finally {
      inflightPromise = null;
    }
  })();
  return inflightPromise;
};

/**
 * Returns { ucId: "ucName" } mapping.
 * Used by useFirebaseData.
 */
export const getUCNameLookup = async () => {
  const docs = await fetchUCDocs();
  const lookup = {};
  docs.forEach((ucData) => {
    const ucId = ucData.uid || ucData.id;
    if (ucId && ucData.uc) {
      lookup[ucId] = ucData.uc;
    }
  });
  return lookup;
};

/**
 * Returns { ucId: { uc, tahsil } } mapping.
 * Used by useComplaints.
 */
export const getUCDetailLookup = async () => {
  const docs = await fetchUCDocs();
  const lookup = {};
  docs.forEach((ucData) => {
    const ucId = ucData.uid || ucData.id;
    if (ucId) {
      lookup[ucId] = {
        uc: ucData.uc || ucData.name || ucData.id,
        tahsil: ucData.tahsil || ''
      };
    }
  });
  return lookup;
};

/**
 * Returns { ucId: { uc, name, tahsil, district } } mapping.
 * Used by FilterContext.
 */
export const getUCFullLookup = async () => {
  const docs = await fetchUCDocs();
  const lookup = {};
  docs.forEach((ucData) => {
    const ucId = ucData.uid || ucData.id;
    if (ucId && ucData.uc) {
      lookup[ucId] = {
        uc: ucData.uc,
        name: ucData.name,
        tahsil: ucData.tahsil,
        district: ucData.district || 'Vehari'
      };
    }
  });
  return lookup;
};

/**
 * Returns full UC records array.
 * Used by useComplaints.fetchUnionCouncils().
 */
export const getUCList = async () => {
  const docs = await fetchUCDocs();
  return docs.map((data) => ({
    id: data.id,
    uid: data.uid || data.id,
    uc: data.uc || data.name || data.id,
    tahsil: data.tahsil || '',
    ...data
  }));
};

/**
 * Invalidate the cache (e.g., after a UC is created/updated).
 */
export const invalidateUCCache = () => {
  cachedDocs = null;
  cacheTimestamp = 0;
};
